// Hook específico para operações de autenticação, usando useRequest
import { useRequest } from './useRequest';
import { User } from '../types/user';
import { LoginResponse } from '../types/auth';
import { useRegister } from './useRegister';
import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { secureStorage } from '../utils/secureStorage';

export function useAuth() {
  const userRequest = useRequest<User>();
  const loginRequest = useRequest<LoginResponse>();
  const registerHook = useRegister();
  const location = useLocation();
  
  // Estado local para loginData, inicializado com base no token armazenado
  const [loginData, setLoginData] = useState<LoginResponse | null>(null);

  // Inicializar loginData do localStorage se token for válido
  useEffect(() => {
    const token = getToken();
    const expiration = secureStorage.getItem('tokenExpiration');
    const created = secureStorage.getItem('tokenCreated');
    
    if (token && expiration && created && Date.now() < new Date(expiration).getTime()) {
      setLoginData({
        authenticated: true,
        accessToken: token,
        expiration,
        created,
      });
    }
  }, []); // Executar apenas uma vez na montagem

  // Função para obter token do localStorage
  const getToken = () => secureStorage.getItem('accessToken');

  // Função para armazenar token, expiração e created
  const setToken = useCallback((token: string, expiration: string, created: string) => {
    secureStorage.setItem('accessToken', token);
    secureStorage.setItem('tokenExpiration', expiration);
    secureStorage.setItem('tokenCreated', created);
  }, []);

  // Função para verificar se token é válido
  const isTokenValid = useCallback(() => {
    const token = getToken();
    const expiration = secureStorage.getItem('tokenExpiration');
    if (!token || !expiration) return false;
    return Date.now() < new Date(expiration).getTime();
  }, []);

  // Função para remover token, expiração e created
  const removeToken = useCallback(() => {
    secureStorage.removeItem('accessToken');
    secureStorage.removeItem('tokenExpiration');
    secureStorage.removeItem('tokenCreated');
  }, []);
  
  const logout = useCallback(() => {
    removeToken();
    setLoginData(null);  // Limpar estado local
    // Outras limpezas se necessário
  }, [removeToken]);

  // Verificar token durante navegação
  useEffect(() => {
    // Só verificar se não estamos na página de login
    if (location.pathname !== '/login' && location.pathname !== '/forgot-password' && location.pathname !== '/reset-password' && location.pathname !== '/set-password') {
      if (!isTokenValid()) {
        console.warn('Token inválido detectado durante navegação. Redirecionando para login.');
        logout();
        // Redirecionamento será feito pelo PrivateRoute
      }
    }
  }, [location.pathname, isTokenValid, logout]);

  // Armazenar token após login bem-sucedido
  useEffect(() => {
    if (loginRequest.data && loginRequest.data.authenticated) {
      setToken(loginRequest.data.accessToken, loginRequest.data.expiration, loginRequest.data.created);
      setLoginData(loginRequest.data);  // Atualizar estado local
      
      // Disparar evento customizado para notificar UserContext
      window.dispatchEvent(new Event('userLoggedIn'));
    }
  }, [loginRequest.data, setToken]);

  // Armazenar token após registro bem-sucedido (login automático)
  useEffect(() => {
    if (registerHook.data && registerHook.data.authenticated) {
      setToken(registerHook.data.accessToken, registerHook.data.expiration, registerHook.data.created);
      setLoginData(registerHook.data);  // Atualizar estado local
    }
  }, [registerHook.data, setToken]);

  const fetchUser = useCallback(async (id: number) => {
    userRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/users/${id}`);
  }, [userRequest]);

  const login = useCallback((email: string, password: string) => {
    loginRequest.execute(`${process.env.REACT_APP_API_BASE_URL}/api/autenticador`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  }, [loginRequest]);


  return {
    // Estados e funções para usuário
    userData: userRequest.data,
    userLoading: userRequest.loading,
    userError: userRequest.error,
    fetchUser,

    // Estados e funções para login (usando estado local para persistência)
    loginData,
    loginLoading: loginRequest.loading,
    loginError: loginRequest.error,
    login,
    logout,
    isTokenValid,
    getToken,

    // Estados e funções para registro
    register: registerHook.register,
    registerLoading: registerHook.loading,
    registerError: registerHook.error,
    emailExists: registerHook.emailExists,
    cpfCnpjExists: registerHook.cpfCnpjExists,
    checkEmailExists: registerHook.checkEmailExists,
    checkCpfCnpjExists: registerHook.checkCpfCnpjExists,
  };
}